import React from 'react';
import styled from 'styled-components';
import { useTheme } from '../context/ThemeContext';

const StyledButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => {
    switch (props.size) {
      case 'sm': return `8px 16px`;
      case 'lg': return `12px 24px`;
      default: return `10px 20px`;
    }
  }};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => {
    switch (props.size) {
      case 'sm': return '13px';
      case 'lg': return '16px';
      default: return '14px';
    }
  }};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: ${props => {
    switch (props.size) {
      case 'sm': return '32px';
      case 'lg': return '48px';
      default: return '40px';
    }
  }};
  letter-spacing: 0.5px; // Slightly increased for readability
  text-transform: uppercase; // Professional field app feel

  background: ${props => {
    if (props.disabled) return props.theme.colors.muted;
    switch (props.variant) {
      case 'secondary': return 'transparent';
      case 'success': return `linear-gradient(180deg, ${props.theme.colors.success}, #0e9f0e)`;
      case 'warning': return `linear-gradient(180deg, ${props.theme.colors.warning}, #e0a800)`;
      case 'danger': return `linear-gradient(180deg, ${props.theme.colors.danger}, #c82333)`;
      case 'info': return props.theme.colors.info;
      default: return props.theme.colors.primary;
    }
  }};

  color: ${props => {
    if (props.disabled) return '#fff';
    if (props.variant === 'secondary') return props.theme.colors.primary || props.theme.colors.tint;
    return '#fff';
  }};

  border: ${props => {
    if (props.variant === 'secondary') return `2px solid ${props.theme.colors.border === '#e2e8f0' ? '#e2e8f0' : '#475569'}`;
    return 'none';
  }};
  
  ${props => props.variant === 'secondary' && `
    border-color: ${props.theme.colors.primary || props.theme.colors.tint};
  `}

  /* Strong shadow for primary buttons */
  box-shadow: ${props =>
    props.variant !== 'secondary' && !props.disabled
      ? '0 2px 4px rgba(35, 37, 98, 0.15), 0 1px 2px rgba(35, 37, 98, 0.08)'
      : 'none'};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: ${props =>
    props.variant !== 'secondary'
      ? '0 4px 8px rgba(35, 37, 98, 0.2), 0 2px 4px rgba(35, 37, 98, 0.1)'
      : 'none'};
    
    ${props => props.variant === 'secondary' && `
      background-color: ${props.theme.colors.primary}10;
    `}

    ${props => props.variant !== 'secondary' && `
      filter: brightness(1.1);
    `}
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    opacity: 0.95;
    box-shadow: none;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
    box-shadow: none;
    background: ${props => props.theme.colors.muted};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}40;
  }
`;

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  onClick,
  type = 'button',
  ...props
}) => {
  const theme = useTheme();

  return (
    <StyledButton
      theme={theme}
      variant={variant}
      size={size}
      disabled={disabled}
      onClick={onClick}
      type={type}
      {...props}
    >
      {children}
    </StyledButton>
  );
};